import { getBriefType_structuredBrief } from '__generated__/getBriefType';
import { DesignerSource } from '__generated__/types';
import { routes } from 'projects/routes';

interface Brief extends getBriefType_structuredBrief {
    designersWithSource?: {
        __typename: 'DesignerWithSource';
        source: DesignerSource;
    }[];
}

export interface BriefStep<T> {
    step: number;
    title: string;
    isAuthRequired?: boolean;
    isDesignerStep?: boolean;
    nextActionLabel?: string;

    // Fields shown on this step – used for filtering down validation errors
    fields?: (keyof T)[];
}

interface Options<T> {
    steps: BriefStep<T>[];
    currentStep: number;
    brief: Brief;
}

export class StepState<T> {
    steps: BriefStep<T>[];
    number: number;
    brief: Brief;

    constructor({ steps, currentStep, brief }: Options<T>) {
        // Remove the find designers step if all our designers are direct
        if (
            brief.designersWithSource &&
            brief.designersWithSource.every(d => d.source == DesignerSource.DIRECT) &&
            brief.designersWithSource.length > 0
        ) {
            steps = steps.filter(step => !step.isDesignerStep);
        }

        this.steps = steps;
        this.brief = brief;
        this.number = currentStep;
    }

    currentStep() {
        const currentStep = this.steps.find(step => step.step === this.number);
        if (currentStep) {
            return currentStep;
        } else {
            throw new Error('Current step does not exist');
        }
    }

    hasStepAfter() {
        return this.steps.map(step => step.step).includes(this.number + 1);
    }

    hasStepBefore() {
        return this.steps.map(step => step.step).includes(this.number - 1);
    }

    nextStep() {
        const nextStep = this.steps.find(step => step.step === this.number + 1);
        return nextStep || this.currentStep();
    }

    prevStep() {
        const prevStep = this.steps.find(step => step.step === this.number - 1);
        return prevStep || this.currentStep();
    }

    nextUrl() {
        return routes.brief(this.brief, this.nextStep().step);
    }

    prevUrl() {
        return routes.brief(this.brief, this.prevStep().step);
    }

    percentageComplete() {
        return (this.number / this.steps.length) * 100;
    }

    stepCount() {
        return this.steps.length;
    }

    nextRequiresAuth() {
        return this.nextStep().isAuthRequired;
    }

    isDesignerStep() {
        return this.currentStep().isDesignerStep;
    }

    nextActionLabel() {
        const nextActionLabel = this.currentStep().nextActionLabel;
        if (nextActionLabel) {
            return nextActionLabel;
        }

        if (!this.hasStepAfter()) {
            return __("Let's do this!");
        } else {
            return __('Next');
        }
    }
}
